import { AnonymousCredential, ContainerClient } from "@azure/storage-blob";
import { Item, ItemFile } from "../core/item";

// Configuration for Azure Blob operations
export interface AzureBlobConfig {
  containerUrl?: string;
  containerClient?: ContainerClient;
}

// Result type for CORS connectivity test
export interface CorsTestResult {
  success: boolean;
  error?: string;
}

// Progress callback type for upload operations
export type UploadProgressCallback = (size: number, loaded: number) => void;

// Cancel function type for upload operations
export type CancelUpload = () => void;

export interface StorageClient { 
  listItems: () => Promise<Item[]>;
  uploadItem: (item: ItemFile, onProgress?: UploadProgressCallback) => Promise<CancelUpload>;
  removeItem: (name: string) => Promise<void>;
}


export const createAzureStorageClient = (sasUrl: string) : StorageClient =>  { 
  const containerClient = createContainerClient(sasUrl);

  return {
    listItems: async (): Promise<Item[]> => {
      return await listItems(containerClient);
    },
    uploadItem: async (
      item: ItemFile, 
      onProgress?: UploadProgressCallback
    ): Promise<CancelUpload> => {
      const controller = new AbortController();
      uploadFile(containerClient, item, controller, onProgress);
      return () => controller.abort();
    },
    removeItem: async (name: string): Promise<void> => {
        await removeFile(containerClient, name);
    }
  }
}


/**
 * Test CORS connectivity before making requests
 */
export const testCorsConnectivity = async (containerUrl: string): Promise<CorsTestResult> => {
  try {
    console.log('Testing CORS for URL:', containerUrl);
    
    // Use the full SAS URL directly - this is the most reliable way
    const response = await fetch(`${containerUrl}&comp=list&restype=container&maxresults=1`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Accept': 'application/xml'
      }
    });
    
    console.log('CORS test response:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    // Azure Storage returns these status codes when CORS is working:
    // - 200: Success (has permissions)
    // - 403: Forbidden (no permissions, but CORS works)
    // - 404: Container not found (but CORS works)
    if (response.status === 200 || response.status === 403 || response.status === 404) {
      return { success: true };
    }
    
    return { 
      success: false, 
      error: `HTTP ${response.status}: ${response.statusText}` 
    };
    
  } catch (error: any) {
    console.log('CORS test error:', error);
    
    if (isCorsError(error)) {
      return { 
        success: false, 
        error: 'CORS not configured. Please configure CORS in your Azure Storage Account.' 
      };
    }
    
    return { 
      success: false, 
      error: `Network error: ${error.message}` 
    };
  }
};

/**
 * Check if error is CORS-related
 */
export const isCorsError = (error: any): boolean => {
  const errorMessage = error?.message || '';
  const errorName = error?.name || '';
  const errorCode = error?.code || '';
  
  return errorMessage.includes('CORS') || 
         errorMessage.includes('Cross-Origin') ||
         errorMessage.includes('Access-Control-Allow-Origin') ||
         errorMessage.includes('preflight request') ||
         errorCode === 'NetworkError' ||
         (errorName === 'TypeError' && errorMessage.includes('fetch')) ||
         errorMessage.includes('ERR_FAILED') ||
         (error?.response && error?.response?.status === 0);
};

/**
 * Get CORS error message with instructions
 */
export const getCorsErrorMessage = (operation: string): string => {
  return `CORS error during ${operation}. 

Azure Storage CORS configuration required:
1. Open your Azure Storage Account
2. Navigate to: Settings → Resource sharing (CORS)
3. Blob service → Add new rule:
   • Allowed origins: http://localhost:3000 (for development) or * (for all)
   • Allowed methods: GET,PUT,POST,DELETE,HEAD,OPTIONS
   • Allowed headers: *
   • Exposed headers: *
   • Max age: 3600
4. Save the settings

Alternative: Use an Azure Function as proxy.`;
};

/**
 * Create a container client from SAS URL
 */
export const createContainerClient = (sasUrl: string): ContainerClient => {
  return new ContainerClient(sasUrl, new AnonymousCredential(), {});
};

/**
 * List all items in storage container
 */
export const listItems = async (containerClient: ContainerClient): Promise<Item[]> => {
  try {
    // Test the connection first by doing a simple operation
    const iterator = containerClient.listBlobsFlat().byPage({ maxPageSize: 1 });
    const firstPage = await iterator.next();
    
    if (firstPage.done) {
      return [];
    }
    
    // If we got here, the connection works, now get all items
    const fullIterator = containerClient.listBlobsFlat().byPage();
    const response = (await fullIterator.next()).value;

    const itemList: Item[] = [];
    const ignoreIndexFilter = (blob: any) => blob.name !== "index.html";
    let i = 0;

    for (const blob of response.segment.blobItems.filter(ignoreIndexFilter)) {
      const blobClient = containerClient.getBlobClient(blob.name);
      itemList.push({
        index: ++i,
        name: blob.name,
        url: blobClient.url,
        createdOn: blob.properties.createdOn
      });
    }

    return itemList;
  } catch (error: any) {
    console.log('Error in listItems:', error);
    console.log('Error details:', {
      message: error.message,
      name: error.name,
      code: error.code,
      stack: error.stack
    });
    throw error;
  }
};

/**
 * Upload a file to blob storage
 */
export const uploadFile = async (
  containerClient: ContainerClient, 
  item: ItemFile, 
  controller: AbortController, 
  onProgress?: UploadProgressCallback
): Promise<void> => {
  const blockBlobClient = containerClient.getBlockBlobClient(item.name);
  await blockBlobClient.uploadData(await item.file.arrayBuffer(), {
    onProgress: (e) => onProgress ? onProgress(item.file.size, e.loadedBytes) : {},
    abortSignal: controller.signal,
    maxSingleShotSize: 1 * 1024 * 1024, // 1MB
    metadata: item.data
  });
};

/**
 * Remove a blob from storage
 */
export const removeFile = async (containerClient: ContainerClient, blobName: string): Promise<void> => {
  await containerClient.deleteBlob(blobName, {});
};
